import { Box, Chip, Tooltip } from '@mui/material';
import { Drone } from '../../types/drone';
import { ownershipOf } from '../../store/slices/dronesSlice';
import { formatDroneStatus } from '../../utils/formatters';

const STATUS_COLORS: Record<
  Drone['status'],
  'success' | 'info' | 'warning' | 'error' | 'default'
> = {
  idle: 'default',
  in_flight: 'success',
  returning: 'info',
  landed: 'default',
  error: 'error',
  offline: 'warning',
  // Yellow for a lost link, red for a shutdown in the air (#799, K2).
  link_lost: 'warning',
  shut_down_in_flight: 'error',
};

/**
 * A drone's status, and who owns it (#385), as the side panel, the report card
 * and the gallery all show them.
 *
 * The ownership chip carries a title rather than colour alone: "you do not own
 * this" is not something to convey by hue.
 */
export const DroneStatusChips: React.FC<{ drone: Drone }> = ({ drone }) => {
  const ownership = ownershipOf(drone);
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Chip
        label={formatDroneStatus(drone.status)}
        color={STATUS_COLORS[drone.status] ?? 'default'}
        size="small"
      />
      {ownership === 'unclaimed' && (
        <Tooltip title="No operator owns this drone — it was registered without anyone signed in. Any operator can see and command it.">
          <Chip label="UNCLAIMED" variant="outlined" size="small" />
        </Tooltip>
      )}
      {/* Somebody else's aircraft (#575). Visible only to a caller holding
          fleet-wide read access, and labelled so that accountability stays
          with the operator it belongs to (#564). */}
      {ownership === 'others' && (
        <Tooltip title="Another operator owns this drone. You can see it because you hold fleet-wide read access; responsibility for it is theirs.">
          <Chip label="ANOTHER OPERATOR'S" variant="outlined" size="small" />
        </Tooltip>
      )}
      {ownership === 'mine' && (
        <Chip label="YOURS" color="primary" variant="outlined" size="small" />
      )}
    </Box>
  );
};
